import type React from 'react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueueItems, useCreateQueueItem, useUpdateQueueItem, QUEUE_STATUSES, QUEUE_REASONS, QUEUE_PRIORITIES } from '../../hooks/useQueueItems'
import type { QueueItem } from '../../lib/database.types'
import { BackLink } from '../../components/BackLink'

const STATUS_LABELS: Record<QueueItem['status'], string> = {
  idea: 'Idea',
  planned: 'Planned',
  printing: 'Printing',
  done: 'Done',
  dropped: 'Dropped',
}

export default function QueueForm() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = Boolean(id)

  const { data: items } = useQueueItems()
  const createItem = useCreateQueueItem()
  const updateItem = useUpdateQueueItem()

  const existing = items?.find((i) => i.id === id)

  const [title, setTitle] = useState(existing?.title ?? '')
  const [status, setStatus] = useState<QueueItem['status']>(existing?.status ?? 'idea')
  const [reason, setReason] = useState(existing?.reason ?? '')
  const [priority, setPriority] = useState<QueueItem['priority']>(existing?.priority ?? 'med')
  const [sourceUrl, setSourceUrl] = useState(existing?.source_url ?? '')
  const [estimatedTimeHours, setEstimatedTimeHours] = useState(
    existing?.estimated_time_hours != null ? String(existing.estimated_time_hours) : '',
  )
  const [estimatedFilamentG, setEstimatedFilamentG] = useState(
    existing?.estimated_filament_g != null ? String(existing.estimated_filament_g) : '',
  )
  const [customerName, setCustomerName] = useState(existing?.customer_name ?? '')
  const [dueDate, setDueDate] = useState(existing?.due_date ?? '')
  const [notes, setNotes] = useState(existing?.notes ?? '')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    const payload = {
      title: title.trim(),
      status,
      reason: reason || null,
      priority,
      source_url: sourceUrl || null,
      estimated_time_hours: estimatedTimeHours ? Number(estimatedTimeHours) : null,
      estimated_filament_g: estimatedFilamentG ? Number(estimatedFilamentG) : null,
      customer_name: customerName || null,
      due_date: dueDate || null,
      notes: notes || null,
    }

    try {
      if (isEdit && id) {
        await updateItem.mutateAsync({ id, ...payload })
      } else {
        await createItem.mutateAsync(payload)
      }
      navigate('/prints/queue')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  const isPending = isEdit ? updateItem.isPending : createItem.isPending

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <BackLink fallback="/prints/queue" />
      <h1>{isEdit ? 'Edit queue item' : 'Add to queue'}</h1>
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        <Field label="Title" value={title} onChange={setTitle} required />

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
            Status
          </label>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {QUEUE_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  background: status === s ? 'var(--color-primary)' : 'var(--color-surface)',
                  color: status === s ? 'white' : 'var(--color-text-muted)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
            Reason
          </label>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {QUEUE_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(reason === r ? '' : r)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  background: reason === r ? 'var(--color-primary)' : 'var(--color-surface)',
                  color: reason === r ? 'white' : 'var(--color-text-muted)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
            Priority
          </label>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {QUEUE_PRIORITIES.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPriority(p)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  background: priority === p ? 'var(--color-primary)' : 'var(--color-surface)',
                  color: priority === p ? 'white' : 'var(--color-text-muted)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <Field label="Source URL (optional)" value={sourceUrl} onChange={setSourceUrl} hint="Link to the model file or reference" />
        <Field label="Estimated print time (h)" value={estimatedTimeHours} onChange={setEstimatedTimeHours} type="decimal" />
        <Field label="Estimated filament (g)" value={estimatedFilamentG} onChange={setEstimatedFilamentG} type="decimal" />
        <Field label="Customer name (optional)" value={customerName} onChange={setCustomerName} />
        <Field label="Due date (optional)" value={dueDate} onChange={setDueDate} type="date" />
        <Field label="Notes (optional)" value={notes} onChange={setNotes} multiline />

        <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)', alignItems: 'center' }}>
          <button
            type="submit"
            disabled={isPending || !title.trim()}
            style={{
              padding: 'var(--space-3) var(--space-5)',
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
            }}
          >
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add to queue'}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
          >
            Cancel
          </button>
        </div>

        {errorMessage && (
          <p style={{ marginTop: 'var(--space-3)', color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {errorMessage}
          </p>
        )}
      </form>
    </div>
  )
}

function Field({ label, value, onChange, type = 'text', hint, required, multiline }: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'date' | 'decimal'
  hint?: string
  required?: boolean
  multiline?: boolean
}) {
  const commonStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
    boxSizing: 'border-box',
  }
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
        {label} {required && <span style={{ color: 'var(--color-danger)' }}>*</span>}
      </label>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} style={commonStyle} />
      ) : (
        <input
          type={type === 'date' ? 'date' : 'text'}
          inputMode={type === 'decimal' ? 'decimal' : undefined}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={commonStyle}
        />
      )}
      {hint && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-1)' }}>{hint}</p>}
    </div>
  )
}
